import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { useChatStore } from "../../stores/chatStore";
interface Reference {
  reference_id: string;
  reference_type: 'table' | 'document' | 'image';
  title: string;
  file_id?: number;
  page_number?: number;
  data?: {
    columns?: string[];
    rows?: Record<string, any>[];
    metadata?: {
      sql?: string;
      row_count?: number;
      question?: string;
    };
  };
  timestamp: Date;
}

interface CitationProps {
  reference: Reference | null;
}

const Citation: React.FC<CitationProps> = ({ reference }) => {
  const [showSql, setShowSql] = useState(false);
  const { documentPreviews, loadDocumentPreview, clearDocumentPreview } = useChatStore();

  const isDocumentReference = reference?.reference_type === "document" && !!reference.file_id;
  const pdfState = isDocumentReference && reference ? documentPreviews[reference.reference_id] : undefined;
  const pdfUrl = pdfState?.url ?? null;
  const pdfLoading = isDocumentReference ? (pdfState?.loading ?? true) : (pdfState?.loading ?? false);
  const pdfError = pdfState?.error ?? null;

  useEffect(() => {
    if (!reference || reference.reference_type !== "document" || !reference.file_id) {
      return;
    }
    loadDocumentPreview(reference);
    return () => clearDocumentPreview(reference.reference_id);
  }, [reference, loadDocumentPreview, clearDocumentPreview]);

  if (!reference) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <p className="text-gray-500 text-center">
          Select a reference to view
        </p>
      </div>
    );
  }

    const handleCopySQL = () => {
    if (reference.data?.metadata?.sql) {
      navigator.clipboard.writeText(reference.data.metadata.sql);
    }
  };

  const handleExportCSV = () => {
    if (!reference.data?.rows || !reference.data?.columns) return;

    const { columns, rows } = reference.data;

    // Create CSV content
    const csvHeader = columns.join(',');
    const csvRows = rows.map(row =>
      columns.map(col => {
        const value = row[col];
        // Handle values with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    );

    const csvContent = [csvHeader, ...csvRows].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (reference.reference_type === 'table') {
    return (
      <div className="flex flex-col gap-1 h-full overflow-auto p-1">
        {/* Top actions (outside table container) */}
        <div className="flex justify-end items-center gap-1 flex-shrink-0" style={{ minHeight: '32px' }}>
          {reference.data?.metadata?.sql && (
            <Button
              icon={showSql ? "pi pi-eye-slash" : "pi pi-eye"}
              text
              rounded
              aria-label="View SQL"
              onClick={() => setShowSql((prev) => !prev)}
              tooltip={showSql ? "Hide SQL" : "View SQL"}
            />
          )}
          {reference.data?.metadata?.sql && (
            <Button
              icon="pi pi-copy"
              text
              rounded
              aria-label="Copy SQL"
              onClick={handleCopySQL}
              tooltip="Copy SQL"
            />
          )}
          <Button
            icon="pi pi-download"
            text
            rounded
            aria-label="Export CSV"
            onClick={handleExportCSV}
            tooltip="Export CSV"
          />
        </div>

        {showSql && reference.data?.metadata?.sql && (
          <pre className="mt-1 text-xs overflow-x-auto p-2 bg-white rounded max-h-32 border border-gray-100">
            {reference.data.metadata.sql}
          </pre>
        )}

        {/* Data Table */}
        <div className="flex-1 overflow-auto min-h-0">
          <DataTable
            value={reference.data?.rows || []}
            scrollable
            scrollHeight="flex"
            tableStyle={{ minWidth: '100%' }}
            className="text-sm"
            emptyMessage="No data available"
            paginator={reference.data?.rows && reference.data.rows.length > 20}
            rows={20}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sortMode="multiple"
            removableSort
          >
            {reference.data?.columns?.map((col) => (
              <Column
                key={col}
                field={col}
                header={col}
                sortable
                style={{ minWidth: '150px' }}
                body={(rowData) => {
                  const value = rowData[col];
                  // Format based on type
                  if (value === null || value === undefined) {
                    return <span className="text-gray-400">-</span>;
                  }
                  if (typeof value === 'number') {
                    // Format numbers with commas
                    return value.toLocaleString();
                  }
                  return String(value);
                }}
              />
            ))}
          </DataTable>
        </div>
      </div>
    );
  }

  if (reference.reference_type === 'document') {
    const displayTitle = reference.page_number
      ? `${reference.title} (Page ${reference.page_number})`
      : reference.title;

    return (
      <div className="flex flex-col gap-4 h-full overflow-hidden p-4">
        <div className="flex justify-between items-start flex-shrink-0">
          <h3 className="text-lg font-semibold m-0">{displayTitle}</h3>
          <Button
            icon="pi pi-external-link"
            label="Open in New Tab"
            size="small"
            outlined
            onClick={() => pdfUrl && window.open(pdfUrl, '_blank')}
            disabled={!pdfUrl}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          {pdfLoading && (
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              Loading document…
            </div>
          )}
          {!pdfLoading && pdfUrl && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0 rounded"
              title={reference.title}
            />
          )}
          {!pdfLoading && !pdfUrl && (
            <div className="w-full h-full flex items-center justify-center text-red-500">
              Unable to load document{pdfError ? `: ${pdfError}` : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <p>Unsupported reference type: {reference.reference_type}</p>
    </div>
  );
};

export default Citation;
